import { hasAnyPermission, useAuthorization } from '@frontend/authorization';
import { ClassType, EntityType, ResolvedEntity, Rights } from '@frontend/common';
import { DefaultPrimaryButton, DefaultSecondaryButton, accountIdFromResolvedEntity } from '@frontend/components/common';
import { ObjectColumnsProps, defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { ConfirmationModal } from '@frontend/components/modal';
import { ConstraintExtendedClient } from '@frontend/constraint/api';
import { ConstraintExtended } from '@frontend/constraint/types';
import { EquipmentStateBadge } from '@frontend/equipment/badge';
import { IdentificationClient } from '@frontend/identification/api';
import { CreateIdentification, Identification, IdentificationType } from '@frontend/identification/types';
import { CommonMessage } from '@frontend/lang';
import { LaundryTransactionClient } from '@frontend/laundry-app/api';
import { LaundryTransaction } from '@frontend/laundry-app/types';
import { ModalType } from '@frontend/modal';
import { PinCodeView } from '@frontend/pin-code/components';
import { PinCodeRendering } from '@frontend/pin-code/types';
import { SlotExtended, SlotStatus } from '@frontend/slot/types';
import { StockExtendedClient } from '@frontend/stock/api';
import { StockExtended } from '@frontend/stock/types';
import { TransactionLendingClient, TransactionPudoExtendedClient, TransactionVendingClient } from '@frontend/transaction/api';
import { TransactionStateBadge } from '@frontend/transaction/common';
import { ExtendedLendingTransaction, ExtendedPudoTransaction, ExtendedVendingTransaction, TransactionStateName } from '@frontend/transaction/types';
import React, { Suspense, lazy, useState } from 'react';
import { LuScanBarcode } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { getVendingTransactionTypeDisplay } from '../../applications/vending/vending-utils';
import { MultiEntityButton } from '../../components/multi-entity-button/multi-entity-button.component';
import { formatConstraintData } from '../../constraint/modal/constraint-detail-sections';
import { DefaultContainer } from '../../modal/detail-modal/default-container/default-container';
import { PlatformDetailGrid } from '../../modal/platform-detail-grid/platform-detail-grid.component';
import { PlatformObjectBadge } from '../../modal/platform-object-badge/platform-object-badge.component';
import UpdateModal from '../../modal/update-modal/update-modal.component';
import useSlotDetailModal from './slot-detail-modal.controller';

const SlotStatusBadge = lazy(() => import('@frontend/slot/badge').then((module) => ({ default: module.SlotStatusBadge })));

export const SlotOpenButton = ({ slot }: { slot: SlotExtended }) => {
    const { openModal, closeModal, openSlotModalId, onOpenSlotConfirmed } = useSlotDetailModal();
    const { permissions } = useAuthorization();
    if (slot.status !== SlotStatus.AVAILABLE || !permissions) return null;
    const canOpen = hasAnyPermission(permissions, [
        { item: EntityType.SLOT, source: EntityType.ACCOUNT, right: Rights.OPEN, uid: slot.account_id },
        { item: EntityType.SLOT, source: EntityType.SPOT, right: Rights.OPEN, uid: slot.spot_id }
    ]);
    if (!canOpen) return null;
    return (
        <DefaultSecondaryButton
            id='SlotDetailModal.OpenButton'
            text={CommonMessage.BUTTONS.OPEN}
            onClick={() =>
                openModal(openSlotModalId, () => (
                    <ConfirmationModal
                        handleClose={() => closeModal(openSlotModalId)}
                        message={CommonMessage.FORMS.OPEN_OBJECT(EntityType.SLOT, slot.display)}
                        onConfirm={() => onOpenSlotConfirmed(slot)}
                        severity={ClassType.WARNING}
                        show={true}
                    />
                ))
            }
        />
    );
};

export const SlotDetailsCard = ({ slot }: { slot: SlotExtended }) => (
    <div className='bg-background-surface outline-border-default mb-4 inline-flex w-full flex-col items-start justify-start gap-6 self-stretch rounded-xl p-4 outline-1 outline-offset-[-1px]'>
        <div className='text-foreground-default justify-start text-xl font-semibold'>{CommonMessage.DETAIL.DETAILS}</div>
        <PlatformDetailGrid
            object={slot}
            cols={3}
            order={['spot', 'module', 'index', 'creation_timestamp', 'update_timestamp', 'status']}
            keyOverwrite={
                new Map([
                    ['spot', CommonMessage.OBJECTS.SPOT.DISTRI_SPOT],
                    ['status', CommonMessage.OBJECTS.COMMON_FIELDS.STATUS]
                ])
            }
            valueOverwrite={
                new Map([
                    [
                        'status',
                        (obj: SlotExtended) => (
                            <Suspense>
                                <SlotStatusBadge status={obj.status} />
                            </Suspense>
                        )
                    ],
                    [
                        'spot',
                        (obj: SlotExtended) => (
                            <PlatformObjectBadge
                                id={obj.spot?.entity_id}
                                display={obj.spot?.name}
                                entityType={EntityType.SPOT}
                                foreign={obj.spot?.foreign}
                                accountId={accountIdFromResolvedEntity(obj.spot)}
                            />
                        )
                    ],
                    [
                        'module',
                        (obj: SlotExtended) => (
                            <PlatformObjectBadge
                                id={obj.module?.entity_id}
                                display={obj.module?.name}
                                entityType={EntityType.MODULE}
                                parentIds={{ spot_id: obj.spot_id }}
                                foreign={obj.module?.foreign}
                                accountId={accountIdFromResolvedEntity(obj.module)}
                            />
                        )
                    ]
                ])
            }
        />
        <SlotOpenButton slot={slot} />
    </div>
);

export const SlotDimensionsSection = ({ slot }: { slot: SlotExtended }) => (
    <div className='inline-flex items-start justify-start gap-4 self-stretch'>
        <DefaultContainer
            styles={{ container: 'min-w-0 flex-1' }}
            title={CommonMessage.OBJECTS.DIMENSION.PLURAL}
            content={
                <div className='flex flex-col items-start justify-start gap-3 self-stretch'>
                    <div className='inline-flex items-start justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.DIMENSION.HEIGHT}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{slot.dimension?.height}</div>
                    </div>
                    <div className='inline-flex items-start justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.DIMENSION.WIDTH}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{slot.dimension?.width}</div>
                    </div>
                    <div className='inline-flex items-start justify-between self-stretch'>
                        <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.DIMENSION.DEPTH}</div>
                        <div className='text-foreground-default justify-start text-base font-medium'>{slot.dimension?.depth}</div>
                    </div>
                </div>
            }
        />
    </div>
);

export const SlotIdentificationSection = ({ slot }: { slot: SlotExtended }) => {
    const viewProps = useSlotDetailModal();
    const { permissions } = useAuthorization();
    const canCreateIdentification =
        permissions != null &&
        viewProps.currentAccount != null &&
        hasAnyPermission(permissions, [{ item: EntityType.IDENTIFICATION, source: EntityType.ACCOUNT, right: Rights.CREATE, uid: viewProps.currentAccount }]);
    return (
        <ReactiveObjectList<Identification>
            key='slot-identification-list'
            fetch={(arg) => IdentificationClient.fetchIdentifications({ entity_id: slot.id, entity_type: EntityType.SLOT, ...arg })}
            label={CommonMessage.OBJECTS.IDENTIFICATION.PLURAL}
            type={EntityType.IDENTIFICATION}
            eventListener={EntityType.IDENTIFICATION}
            dispatch={viewProps.dispatch}
            customHeaderComponent={
                canCreateIdentification ? (
                    <div className='m-2'>
                        <DefaultPrimaryButton
                            text={
                                <FormattedMessage
                                    id='slot-detail-modal.add-identification'
                                    description='Button label to add a new identification on the SLOT detail modal'
                                    defaultMessage='Add identification'
                                />
                            }
                            onClick={() =>
                                viewProps.openModal('slot-identification-create', () => (
                                    <UpdateModal<Identification, CreateIdentification>
                                        order={['key', 'valid_timestamp']}
                                        required={['key']}
                                        values={{
                                            key: undefined,
                                            type: IdentificationType.ALIAS,
                                            entity_id: slot.id,
                                            entity_type: EntityType.SLOT
                                        }}
                                        fieldProps={() => new Map<keyof CreateIdentification, any>([['valid_timestamp', { type: 'datetime' }]])}
                                        onSubmit={(v) => IdentificationClient.postIdentification(slot.account_id, v)}
                                        type={EntityType.IDENTIFICATION}
                                        label={
                                            <FormattedMessage
                                                id='slot-detail-modal.add-identification'
                                                description='Button label to add a new identification on the SLOT detail modal'
                                                defaultMessage='Add identification'
                                            />
                                        }
                                    />
                                ))
                            }
                        />
                    </div>
                ) : null
            }
            columns={{
                columns: ['key', 'type', 'valid_timestamp'],
                customActionButtons: [
                    {
                        label: (
                            <FormattedMessage
                                id='slot-detail-modal.show-identification'
                                description='Row action label to show the identification code as a barcode or QR code on the SLOT detail modal'
                                defaultMessage='Show identification'
                            />
                        ),
                        icon: LuScanBarcode,
                        classType: ClassType.INFO,
                        onClick: (identification) =>
                            viewProps.openModal(
                                viewProps.showIdentificationModalId,
                                () => <IdentificationView identification={identification.key} />,
                                undefined,
                                {
                                    type: ModalType.PROMPT,
                                    className: 'bg-background-surface flex justify-center rounded-md p-6 align-middle'
                                }
                            ),
                        requiredPermissions: viewProps.currentAccount
                            ? [
                                  {
                                      item: EntityType.IDENTIFICATION,
                                      source: EntityType.ACCOUNT,
                                      right: Rights.READ,
                                      uid: viewProps.currentAccount
                                  }
                              ]
                            : undefined
                    }
                ],
                delete: {
                    deleteAction: (accountId, id) => IdentificationClient.deleteIdentification(accountId, id),
                    deleteKeys: ['account_id', 'id'],
                    requiredPermissions: viewProps.currentAccount
                        ? [
                              {
                                  item: EntityType.IDENTIFICATION,
                                  source: EntityType.ACCOUNT,
                                  right: Rights.DELETE,
                                  uid: viewProps.currentAccount
                              }
                          ]
                        : undefined
                }
            }}
            tableProps={{ defaultHeight: true }}
        />
    );
};

export const SlotActivityPudoSection = ({ slot }: { slot: SlotExtended }) => {
    const { dispatch } = useSlotDetailModal();
    return (
        <ReactiveObjectList<ExtendedPudoTransaction>
            key='slot-activity-pudo'
            fetch={(arg) =>
                TransactionPudoExtendedClient.fetchPudoExtendedTransactions({
                    ...arg,
                    account_id: slot.account_id,
                    slot_id: slot.id,
                    size: '8',
                    without_collis: 'true',
                    without_packages: 'true',
                    without_pin_codes: 'true'
                })
            }
            label={ActiveTransactions}
            columns={{
                columns: ['senders', 'receivers', 'state'],
                headerOverwrite: new Map([
                    ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                    ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: createParticipantActivityCells<ExtendedPudoTransaction>()
            }}
            dispatch={dispatch}
            type={EntityType.TRANSACTION}
            tableProps={{ hideFooter: true, maxHeight: true }}
        />
    );
};

export const SlotActivityLaundrySection = ({ slot }: { slot: SlotExtended }) => {
    const { dispatch } = useSlotDetailModal();
    return (
        <ReactiveObjectList<LaundryTransaction>
            key='slot-activity-laundry'
            fetch={(arg) =>
                LaundryTransactionClient.fetchLaundryTransactions(slot.account_id, {
                    ...arg,
                    slot_id: slot.id,
                    size: '8'
                })
            }
            label={RecentShipments}
            columns={{
                columns: ['senders', 'receivers', 'state'],
                headerOverwrite: new Map([
                    ['receivers', CommonMessage.OBJECTS.TRANSACTION.RECEIVERS],
                    ['senders', CommonMessage.OBJECTS.TRANSACTION.SENDERS],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: createParticipantActivityCells<LaundryTransaction>()
            }}
            dispatch={dispatch}
            type={EntityType.TRANSACTION}
            tableProps={{ hideFooter: true, maxHeight: true }}
        />
    );
};

export const SlotActivityVendingSection = ({ slot }: { slot: SlotExtended }) => {
    const { dispatch } = useSlotDetailModal();
    return (
        <ReactiveObjectList<ExtendedVendingTransaction>
            key='slot-activity-vending'
            fetch={(arg) =>
                TransactionVendingClient.getVendingTransactions({
                    ...arg,
                    slot_id: slot.id,
                    size: '8'
                })
            }
            label={RecentVendings}
            columns={{
                columns: ['ean', 'sku', 'product', 'quantity', 'state', 'type'],
                headerOverwrite: new Map([
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE],
                    ['ean', CommonMessage.OBJECTS.PRODUCT.EAN],
                    ['sku', CommonMessage.OBJECTS.PRODUCT.SKU],
                    ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY],
                    ['type', CommonMessage.OBJECTS.COMMON_FIELDS.TYPE]
                ]),
                customCells: vendingActivityCells
            }}
            dispatch={dispatch}
            type={EntityType.TRANSACTION}
            tableProps={{ hideFooter: true, maxHeight: true }}
        />
    );
};

export const SlotActivityLendingSection = ({ slot }: { slot: SlotExtended }) => {
    const { dispatch, viewDetailedLendingTransactionsList } = useSlotDetailModal();
    return (
        <ReactiveObjectList<ExtendedLendingTransaction>
            key='slot-activity-lending'
            fetch={(arg) =>
                TransactionLendingClient.fetchLendingTransactions({
                    ...arg,
                    account_id: slot.account_id,
                    slot_id: slot.id,
                    size: '8'
                })
            }
            label={RecentUtilizations}
            columns={{
                columns: ['product', 'equipment', 'user', 'quantity', 'state'],
                headerOverwrite: new Map([
                    ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['equipment', CommonMessage.OBJECTS.EQUIPMENT.DEFAULT],
                    ['user', CommonMessage.OBJECTS.USER.DEFAULT],
                    ['quantity', CommonMessage.OBJECTS.COMMON_FIELDS.QUANTITY],
                    ['state', CommonMessage.OBJECTS.COMMON_FIELDS.STATE]
                ]),
                customCells: lendingActivityCells
            }}
            dispatch={dispatch}
            type={EntityType.TRANSACTION}
            tableProps={{
                maxHeight: true,
                customFooter: (
                    <div className='flex gap-3.5'>
                        <DefaultSecondaryButton
                            text={CommonMessage.BUTTONS.VIEW_DETAILED_LIST}
                            onClick={() => viewDetailedLendingTransactionsList(slot.id)}
                        />
                    </div>
                )
            }}
        />
    );
};

export const SlotContentsSection = ({ slot }: { slot: SlotExtended }) => {
    const { dispatch } = useSlotDetailModal();
    return (
        <ReactiveObjectList<StockExtended>
            key='slot-content'
            label={
                <FormattedMessage
                    id='spots.contents.title'
                    defaultMessage='Contents'
                />
            }
            fetch={(arg) => StockExtendedClient.fetchSpotSlotStocksExtended(slot.account_id, slot.spot_id, slot.module_id, slot.id, arg)}
            columns={{
                columns: ['product', 'equipment', 'equipment_state', 'ean', 'sku'],
                headerOverwrite: new Map([
                    ['product', CommonMessage.OBJECTS.PRODUCT.DEFAULT],
                    ['equipment', CommonMessage.OBJECTS.EQUIPMENT.DEFAULT],
                    ['equipment_state', CommonMessage.OBJECTS.EQUIPMENT.STATUS]
                ]),
                customCells: new Map([
                    [
                        'product',
                        (cell) =>
                            cell.row.original.product ? (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        id={cell.row.original.product.entity_id}
                                        display={cell.row.original.product.name}
                                        entityType={EntityType.PRODUCT}
                                        key={cell.row.original.product.entity_id}
                                        foreign={cell.row.original.product.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.product)}
                                    />
                                </td>
                            ) : (
                                <td className={defaultCellClassName}>/</td>
                            )
                    ],
                    [
                        'equipment',
                        (cell) =>
                            cell.row.original.equipment ? (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        id={cell.row.original.equipment.entity_id}
                                        display={cell.row.original.equipment.name}
                                        entityType={EntityType.EQUIPMENT}
                                        key={cell.row.original.equipment.entity_id}
                                        foreign={cell.row.original.equipment.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.equipment)}
                                    />
                                </td>
                            ) : (
                                <td className={defaultCellClassName}>/</td>
                            )
                    ],
                    [
                        'equipment_state',
                        (cell) =>
                            cell.row.original.equipment_state ? (
                                <td className={defaultCellClassName}>
                                    <EquipmentStateBadge state={cell.row.original.equipment_state} />
                                </td>
                            ) : (
                                <td className={defaultCellClassName}>/</td>
                            )
                    ]
                ])
            }}
            dispatch={dispatch}
            type={EntityType.STOCK}
            tableProps={{ hideFooter: true, defaultHeight: true }}
        />
    );
};

export const SlotConstraintsSection = ({ slot }: { slot: SlotExtended }) => {
    const { dispatch, viewDetailedConstraintsList } = useSlotDetailModal();
    return (
        <ReactiveObjectList<ConstraintExtended>
            key='slot-constraints'
            label={CommonMessage.OBJECTS.CONSTRAINT.PLURAL}
            fetch={(arg) => ConstraintExtendedClient.fetchExtendedApplicableConstraints(slot.account_id, slot.spot_id, { ...arg, slot_id: slot.id })}
            type={EntityType.CONSTRAINT}
            columns={{
                columns: ['module', 'slot', 'data'],
                headerOverwrite: new Map([['data', CommonMessage.OBJECTS.CONSTRAINT.DEFAULT]]),
                customCells: new Map([
                    [
                        'module',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                {cell.row.original.module ? (
                                    <PlatformObjectBadge
                                        id={cell.row.original.module.entity_id}
                                        display={cell.row.original.module.name}
                                        entityType={EntityType.MODULE}
                                        key={cell.row.original.module.entity_id}
                                        foreign={cell.row.original.module.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.module)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'slot',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                {cell.row.original.slot ? (
                                    <PlatformObjectBadge
                                        id={cell.row.original.slot.entity_id}
                                        display={cell.row.original.slot.name}
                                        entityType={EntityType.SLOT}
                                        key={cell.row.original.slot.entity_id}
                                        foreign={cell.row.original.slot.foreign}
                                        accountId={accountIdFromResolvedEntity(cell.row.original.slot)}
                                    />
                                ) : (
                                    <span>/</span>
                                )}
                            </td>
                        )
                    ],
                    [
                        'data',
                        (cell) => (
                            <td className={defaultCellClassName}>
                                <div className='flex gap-1'>
                                    <span>{(cell.row.original.type.charAt(0).toUpperCase() + cell.row.original.type.slice(1)).replace(/_/g, ' ')}:</span>
                                    <span>{formatConstraintData(cell.row.original.data, cell.row.original.type)}</span>
                                </div>
                            </td>
                        )
                    ]
                ])
            }}
            dispatch={dispatch}
            tableProps={{
                defaultHeight: true,
                customFooter: (
                    <div className='flex gap-3.5'>
                        <DefaultSecondaryButton
                            text={CommonMessage.BUTTONS.VIEW_DETAILED_LIST}
                            onClick={() => viewDetailedConstraintsList(slot.id)}
                        />
                    </div>
                )
            }}
        />
    );
};

export const ActiveTransactions = (
    <FormattedMessage
        id='pudo.active-transactions'
        description='Label for lists of active PuDo transactions shown in activity sections'
        defaultMessage='Active transactions'
    />
);

export const RecentShipments = (
    <FormattedMessage
        id='slot.recentShipments'
        description='Label for recent laundry shipments shown in the SLOT detail activity section'
        defaultMessage='Recent Shipments'
    />
);

export const RecentVendings = (
    <FormattedMessage
        id='slot.recentVendings'
        description='Label for recent vending transactions shown in the SLOT detail activity section'
        defaultMessage='Recent Vendings'
    />
);

export const RecentUtilizations = (
    <FormattedMessage
        id='slot.recentUtilizations'
        description='Label for recent lending utilizations shown in the SLOT detail activity section'
        defaultMessage='Recent utilizations'
    />
);

export const lendingActivityCells: NonNullable<ObjectColumnsProps<ExtendedLendingTransaction>['customCells']> = new Map([
    [
        'state',
        (obj) => {
            return (
                <td
                    key={obj.row.original.state}
                    className='text-foreground-default truncate p-2 text-sm font-medium'>
                    <TransactionStateBadge state={obj.row.original.state} />
                </td>
            );
        }
    ],
    [
        'product',
        (obj) => {
            if (!obj.row.original.product) {
                return (
                    <td className={defaultCellClassName}>
                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                    </td>
                );
            }

            return (
                <td className={defaultCellClassName}>
                    <PlatformObjectBadge
                        entityType={EntityType.PRODUCT}
                        id={obj.row.original.product.entity_id}
                        display={obj.row.original.product.name}
                        key={obj.row.original.product.entity_id}
                        foreign={obj.row.original.product.foreign}
                        accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                    />
                </td>
            );
        }
    ],
    [
        'equipment',
        (obj) => {
            if (!obj.row.original.equipment) {
                return (
                    <td className={defaultCellClassName}>
                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                    </td>
                );
            }

            return (
                <td className={defaultCellClassName}>
                    <PlatformObjectBadge
                        entityType={EntityType.EQUIPMENT}
                        id={obj.row.original.equipment.entity_id}
                        display={obj.row.original.equipment.name}
                        key={obj.row.original.equipment.entity_id}
                        foreign={obj.row.original.equipment.foreign}
                        accountId={accountIdFromResolvedEntity(obj.row.original.equipment)}
                    />
                </td>
            );
        }
    ],
    [
        'user',
        (obj) => {
            if (!obj.row.original.user) {
                return (
                    <td className={defaultCellClassName}>
                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                    </td>
                );
            }

            return (
                <td className={defaultCellClassName}>
                    <PlatformObjectBadge
                        entityType={EntityType.USER}
                        id={obj.row.original.user.entity_id}
                        display={obj.row.original.user.name}
                        key={obj.row.original.user.entity_id}
                        foreign={obj.row.original.user.foreign}
                        accountId={accountIdFromResolvedEntity(obj.row.original.user)}
                    />
                </td>
            );
        }
    ]
]);

export function createParticipantActivityCells<
    T extends {
        id: string;
        senders: ResolvedEntity[] | null;
        receivers: ResolvedEntity[] | null;
        state: TransactionStateName;
    }
>(): NonNullable<ObjectColumnsProps<T>['customCells']> {
    return new Map([
        [
            'senders',
            (obj) => {
                if (obj.row.original.senders === null || obj.row.original.senders.length === 0) {
                    return (
                        <td className='text-brand-primary align-center truncate p-2 text-xs'>
                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                        </td>
                    );
                }
                return (
                    <td
                        key={obj.row.original.senders[0].entity_id}
                        className='text-brand-primary align-center truncate p-2 text-xs'>
                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                            <PlatformObjectBadge
                                id={obj.row.original.senders[0].entity_id}
                                entityType={EntityType.USER}
                                display={obj.row.original.senders[0].name}
                                foreign={obj.row.original.senders[0].foreign}
                                accountId={accountIdFromResolvedEntity(obj.row.original.senders[0])}
                                key={obj.row.original.senders[0].entity_id}
                            />
                            {obj.row.original.senders.length > 1 && (
                                <MultiEntityButton
                                    entities={obj.row.original.senders.slice(1)}
                                    render={(entity) => (
                                        <PlatformObjectBadge
                                            key={entity.entity_id}
                                            id={entity.entity_id}
                                            entityType={EntityType.USER}
                                            display={entity.name}
                                            foreign={entity.foreign}
                                            accountId={accountIdFromResolvedEntity(entity)}
                                        />
                                    )}
                                />
                            )}
                        </div>
                    </td>
                );
            }
        ],
        [
            'receivers',
            (obj) => {
                if (obj.row.original.receivers === null || obj.row.original.receivers.length === 0) {
                    return (
                        <td className='text-brand-primary align-center truncate p-2 text-xs'>
                            <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                        </td>
                    );
                }
                return (
                    <td
                        key={obj.row.original.receivers[0].entity_id}
                        className='text-brand-primary align-center truncate p-2 text-xs'>
                        <div className='flex gap-2.5 truncate text-sm font-medium'>
                            <PlatformObjectBadge
                                id={obj.row.original.receivers[0].entity_id}
                                entityType={EntityType.USER}
                                display={obj.row.original.receivers[0].name}
                                foreign={obj.row.original.receivers[0].foreign}
                                accountId={accountIdFromResolvedEntity(obj.row.original.receivers[0])}
                                key={obj.row.original.receivers[0].entity_id}
                            />
                            {obj.row.original.receivers.length > 1 && (
                                <MultiEntityButton
                                    entities={obj.row.original.receivers.slice(1)}
                                    render={(entity) => (
                                        <PlatformObjectBadge
                                            key={entity.entity_id}
                                            id={entity.entity_id}
                                            entityType={EntityType.USER}
                                            display={entity.name}
                                            foreign={entity.foreign}
                                            accountId={accountIdFromResolvedEntity(entity)}
                                        />
                                    )}
                                />
                            )}
                        </div>
                    </td>
                );
            }
        ],
        [
            'state',
            (obj) => {
                return (
                    <td
                        key={obj.row.original.state}
                        className='text-foreground-default truncate p-2 text-sm font-medium'>
                        <TransactionStateBadge state={obj.row.original.state} />
                    </td>
                );
            }
        ]
    ]);
}

export const IdentificationView = (props: { identification: string }) => {
    const options = [PinCodeRendering.BARCODE_128, PinCodeRendering.QR_CODE, PinCodeRendering.QR_CODE_AND_TEXT, PinCodeRendering.TEXT];
    const [selected, setSelected] = useState<PinCodeRendering>(PinCodeRendering.QR_CODE);

    return (
        <div className='flex w-full flex-col items-center gap-6'>
            <div className='flex flex-row flex-wrap items-center justify-center gap-2'>
                {options.map((rendering) => (
                    <button
                        onClick={() => setSelected(rendering)}
                        key={rendering}
                        type='button'
                        className={`btn ${selected === rendering ? 'btn-brand-primary' : 'btn-brand-muted'}`}>
                        {rendering.replace('_', ' ')}
                    </button>
                ))}
            </div>
            <div className='flex w-full max-w-sm items-center justify-center'>
                <PinCodeView
                    codeOverwrite={props.identification}
                    renderingOverwrite={selected}
                />
            </div>
        </div>
    );
};

export const vendingActivityCells: NonNullable<ObjectColumnsProps<ExtendedVendingTransaction>['customCells']> = new Map([
    [
        'state',
        (obj) => {
            return (
                <td
                    key={obj.row.original.state}
                    className='text-foreground-default truncate p-2 text-sm font-medium'>
                    <TransactionStateBadge state={obj.row.original.state} />
                </td>
            );
        }
    ],
    [
        'product',
        (obj) => {
            if (obj.row.original.product === null) {
                return (
                    <td className='text-brand-primary align-center truncate p-2 text-xs'>
                        <div className='text-foreground-default justify-start text-sm font-medium'>/</div>
                    </td>
                );
            }
            return (
                <td
                    key={obj.row.original.product.entity_id}
                    className='text-brand-primary align-center truncate p-2 text-xs'>
                    <div className='flex gap-2.5 truncate text-sm font-medium'>
                        <PlatformObjectBadge
                            id={obj.row.original.product.entity_id}
                            entityType={EntityType.PRODUCT}
                            display={obj.row.original.product.name}
                            key={obj.row.original.product.entity_id}
                            foreign={obj.row.original.product.foreign}
                            accountId={accountIdFromResolvedEntity(obj.row.original.product)}
                        />
                    </div>
                </td>
            );
        }
    ],
    [
        'type',
        (obj) => {
            return (
                <td className={defaultCellClassName}>
                    <div className='text-foreground-default justify-start text-sm font-medium'>
                        {getVendingTransactionTypeDisplay(obj.row.original.feature)}
                    </div>
                </td>
            );
        }
    ]
]);
